/**
 * MODULE: domain/pages/frontend/components/editor/PagesTocBlockView.tsx
 * PURPOSE: Live table-of-contents block that links to headings on the current page
 */

import { NodeViewWrapper } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import { useCallback, useEffect, useState } from 'react';
import { Label, Select, TextInput } from 'flowbite-react';
import {
  DEFAULT_TOC_TITLE,
  extractHeadingsFromEditor,
  filterHeadingsByDepth,
  normalizeTocMaxDepth,
  scrollToTocHeading,
  tocHeadingIndentClass,
  type TocHeading,
} from '../../utils/pageTocUtils';

export function PagesTocBlockView({ node, selected, updateAttributes, editor }: NodeViewProps) {
  const editable = editor.isEditable;
  const title = String(node.attrs.title ?? DEFAULT_TOC_TITLE);
  const maxDepth = normalizeTocMaxDepth(node.attrs.maxDepth);
  const [headings, setHeadings] = useState<TocHeading[]>([]);

  const refreshHeadings = useCallback(() => {
    const next = filterHeadingsByDepth(extractHeadingsFromEditor(editor), maxDepth);
    setHeadings(next);
  }, [editor, maxDepth]);

  useEffect(() => {
    refreshHeadings();
    if (!editor || editor.isDestroyed) {
      return undefined;
    }

    const handleUpdate = () => refreshHeadings();
    editor.on('update', handleUpdate);
    editor.on('selectionUpdate', handleUpdate);
    return () => {
      editor.off('update', handleUpdate);
      editor.off('selectionUpdate', handleUpdate);
    };
  }, [editor, refreshHeadings]);

  const handleHeadingClick = useCallback(
    (heading: TocHeading) => {
      scrollToTocHeading(editor, heading);
    },
    [editor]
  );

  return (
    <NodeViewWrapper
      className={`my-4 ${selected ? 'ring-2 ring-blue-300 ring-offset-2 dark:ring-blue-700 dark:ring-offset-gray-950' : ''}`}
      data-testid="pages-toc-block"
      data-heading-count={headings.length}
    >
      <nav
        aria-label={title}
        className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-900/40"
      >
        {editable ? (
          <div className="mb-3 flex flex-wrap items-end gap-3">
            <div className="min-w-[160px] flex-1">
              <Label htmlFor={`pages-toc-title-${node.attrs.pagesBlockId ?? 'new'}`}>Title</Label>
              <TextInput
                id={`pages-toc-title-${node.attrs.pagesBlockId ?? 'new'}`}
                sizing="sm"
                value={title}
                onChange={(event) => updateAttributes({ title: event.target.value })}
                data-testid="pages-toc-title-input"
              />
            </div>
            <div className="min-w-[140px]">
              <Label htmlFor={`pages-toc-depth-${node.attrs.pagesBlockId ?? 'new'}`}>Show levels</Label>
              <Select
                id={`pages-toc-depth-${node.attrs.pagesBlockId ?? 'new'}`}
                sizing="sm"
                value={String(maxDepth)}
                onChange={(event) =>
                  updateAttributes({ maxDepth: normalizeTocMaxDepth(Number(event.target.value)) })
                }
                data-testid="pages-toc-depth-select"
              >
                <option value="1">Heading 1 only</option>
                <option value="2">Headings 1–2</option>
                <option value="3">Headings 1–3</option>
              </Select>
            </div>
          </div>
        ) : (
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {title}
          </p>
        )}

        {headings.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="pages-toc-empty">
            Add headings to this page to build a table of contents.
          </p>
        ) : (
          <ul className="space-y-1" data-testid="pages-toc-list">
            {headings.map((heading) => (
              <li key={`${heading.blockId ?? 'pos'}-${heading.pos}`} className={tocHeadingIndentClass(heading.level)}>
                <button
                  type="button"
                  className="w-full truncate text-left text-sm text-blue-700 hover:text-blue-900 hover:underline dark:text-blue-300 dark:hover:text-blue-100"
                  data-testid={
                    heading.blockId ? `pages-toc-link-${heading.blockId}` : `pages-toc-link-pos-${heading.pos}`
                  }
                  data-level={heading.level}
                  onClick={() => handleHeadingClick(heading)}
                >
                  {heading.text}
                </button>
              </li>
            ))}
          </ul>
        )}
      </nav>
    </NodeViewWrapper>
  );
}
